import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { adminService } from "../../features/admin/service";
import type {
  MarketPulseFilterOptions, MarketPulseSummary, MarketPulseTab, PriceCategory, DealCategory,
  GeoRollupLevel, GeoRollupRow, PriceTrendPoint,
} from "../../features/admin/types";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";

const GEO_LEVEL_ORDER: GeoRollupLevel[] = ["continent", "subregion", "country", "city"];
const GEO_LEVEL_LABEL: Record<GeoRollupLevel, string> = {
  continent: "Continent", subregion: "Region", country: "Country", city: "City",
};

const TAB_LABEL: Record<MarketPulseTab, string> = {
  itinerary: "Itinerary", flyer: "Flyer", video: "Video", rag_chat: "RAG Chat", deal: "Deal",
};
const DEAL_CATEGORY_LABEL: Record<DealCategory, string> = {
  package: "Package", flight: "Flight", cruise: "Cruise", transfer: "Transfer",
  experience: "Experience", service: "Service", stay: "Hotel/Stay",
};
const PRICE_TIER_LABEL: Record<PriceCategory, string> = {
  budget: "Budget", mid: "Mid", premium: "Premium", luxury: "Luxury",
};
const PRICE_TIER_COLOR: Record<PriceCategory, string> = {
  budget: "#34d399", mid: "#60a5fa", premium: "#a78bfa", luxury: "#f59e0b",
};

type DatePreset = "all" | "7d" | "30d" | "90d" | "custom";
const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: "all", label: "All time" },
  { key: "7d", label: "Past 7 days" },
  { key: "30d", label: "Past 30 days" },
  { key: "90d", label: "Past 90 days" },
  { key: "custom", label: "Custom range" },
];
const PRESET_MS: Record<Exclude<DatePreset, "all" | "custom">, number> = {
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
  "90d": 90 * 24 * 60 * 60 * 1000,
};
const localToIso = (v: string): string | undefined => (v ? new Date(v).toISOString() : undefined);

const nfmt = (n: number) => (n ?? 0).toLocaleString();

const tabCount = (row: GeoRollupRow, tab: MarketPulseTab): number =>
  row.by_tab.find((t) => t.tab === tab)?.count ?? 0;
// All 5 sources, deal included — full segregation in one place (vs. the main table above,
// which stays blended). "deal" first since it's the one real listing source; the other 4
// are search/generation demand about a destination, not a listing.
const SOURCE_TABS: MarketPulseTab[] = ["deal", "rag_chat", "itinerary", "flyer", "video"];
const SOURCE_TAB_COLOR: Record<string, string> = {
  deal: "#818cf8", rag_chat: "#6366f1", itinerary: "#34d399", flyer: "#f59e0b", video: "#f472b6",
};

// p10-p90 band, not min-max — trims outliers so the range reads as "how the market
// is actually behaving" rather than one freak listing stretching it unreadable.
const fmtBand = (p10: number | null, p90: number | null): string => {
  if (p10 == null || p90 == null) return "—";
  return p10 === p90 ? `₹${p10.toLocaleString()}` : `₹${p10.toLocaleString()} – ₹${p90.toLocaleString()}`;
};
const fmtInr = (v: number | null): string => (v == null ? "—" : `₹${v.toLocaleString()}`);

// "as of 2h ago" / "as of Aug 12" — short relative-ish label for the current-price figure,
// so it reads as "this is real and recent" rather than an unexplained bare number.
const fmtAsOf = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 14) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// "2026-07-15" (daily bucket) or "2026-W29" (ISO week bucket) -> "Jul 15" / "Jul 13–19".
// Recharts renders the raw dataKey string verbatim with no reformatting, which is why the
// timeseries axis used to show unreadable stacked "2026-07-15" labels for every point.
const fmtAxisDate = (raw: string): string => {
  const weekMatch = /^(\d{4})-W(\d{2})$/.exec(raw);
  if (weekMatch) {
    const [, yearStr, weekStr] = weekMatch;
    const jan4 = new Date(Date.UTC(Number(yearStr), 0, 4));
    const weekStart = new Date(jan4);
    weekStart.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (Number(weekStr) - 1) * 7);
    return weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  }
  const d = new Date(raw.length <= 10 ? `${raw}T00:00:00Z` : raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
};

const DATE_PRESET_LABEL_SHORT: Record<DatePreset, string> = {
  all: "all time", "7d": "past 7 days", "30d": "past 30 days", "90d": "past 90 days", custom: "custom range",
};

const Select: React.FC<{
  value: string; onChange: (v: string) => void; options: { value: string; label: string }[];
  placeholder: string; disabled?: boolean;
}> = ({ value, onChange, options, placeholder, disabled }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-40 focus:outline-none focus:border-indigo-500/50 min-w-[140px]"
  >
    <option value="all">{placeholder}</option>
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

interface GeoPathStep { level: GeoRollupLevel; key: string; name: string; }

const AdminMarketPulsePage: React.FC = () => {
  const [tab, setTab] = useState("all");
  const [priceCategory, setPriceCategory] = useState("all");
  const [category, setCategory] = useState("all");

  // The geo drill-down IS the geo filter for the whole page (summary/graph/heatmap
  // included) — clicking a continent narrows everything to it, drilling into a region
  // narrows further, etc. No separate continent/region/country/city dropdowns alongside
  // this — one geo navigation, not two that could disagree with each other.
  const [geoPath, setGeoPath] = useState<GeoPathStep[]>([]);
  const currentGeoLevel: GeoRollupLevel = GEO_LEVEL_ORDER[geoPath.length];
  const [geoRows, setGeoRows] = useState<GeoRollupRow[]>([]);
  const [loadingGeo, setLoadingGeo] = useState(true);

  // Defaults to 30d, not "all time" — a B2B price view should read as "what's the market
  // doing lately," not an undated all-history blend of stale and current listings.
  const [datePreset, setDatePreset] = useState<DatePreset>("30d");
  const [dateMenuOpen, setDateMenuOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [filterOptions, setFilterOptions] = useState<MarketPulseFilterOptions | null>(null);
  const [summary, setSummary] = useState<MarketPulseSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Price Trend chart — independent geo/category pick from the geo drill-down above (a
  // price trend is meaningless without picking ONE destination to look at, but "one
  // destination" can be a continent, a region, a country, or a city — same 4 levels the
  // drill-down table already uses), same tab/date filters as the rest of the page.
  const [trendGeoLevel, setTrendGeoLevel] = useState<GeoRollupLevel>("country");
  const [trendGeoKey, setTrendGeoKey] = useState("all");
  const [trendCategory, setTrendCategory] = useState("all");
  const [trendPoints, setTrendPoints] = useState<PriceTrendPoint[]>([]);
  const [loadingTrend, setLoadingTrend] = useState(false);
  // Route origin — only shown/used when trendCategory="flight". A flight's price is a
  // function of the ROUTE (A->B), not just the destination — "flights to Thailand" blends
  // a ₹8k Bangkok hop from Delhi with a ₹90k one from a Europe layover into one meaningless
  // number. Every other category is destination-only (package/stay/experience/etc. don't
  // have a route concept), so this stays hidden for them.
  const [trendOriginLevel, setTrendOriginLevel] = useState<"country" | "city">("country");
  const [trendOriginKey, setTrendOriginKey] = useState("all");
  // Only flight exposes the route (from/to) picker right now — transfer and cruise are
  // route-shaped the same way and capture is fixed (see the TBD-placeholder fix in the deal
  // hook), but deliberately left out of this category dropdown for now (not yet surfaced).
  const isRouteCategory = trendCategory === "flight";

  const fetchFilterOptions = useCallback(() => {
    return adminService.getMarketPulseFilterOptions({}).then(setFilterOptions).catch(console.error);
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const geoParentFilters = () => {
    const byLevel: Record<string, string> = {};
    for (const step of geoPath) {
      if (step.level === "continent") byLevel.continent = step.key;
      if (step.level === "subregion") byLevel.subregion = step.key;
      if (step.level === "country") byLevel.country_iso2 = step.key;
    }
    return byLevel;
  };

  const buildFilters = () => {
    let date_from: string | undefined;
    let date_to: string | undefined;
    if (datePreset === "custom") {
      date_from = localToIso(customFrom);
      date_to = localToIso(customTo);
    } else if (datePreset !== "all") {
      date_from = new Date(Date.now() - PRESET_MS[datePreset]).toISOString();
    }
    const geo = geoParentFilters();
    const city = geoPath.find((s) => s.level === "city")?.key;
    return {
      tab: tab === "all" ? undefined : tab,
      continent: geo.continent,
      subregion: geo.subregion,
      country_iso2: geo.country_iso2,
      city,
      price_category: priceCategory === "all" ? undefined : priceCategory,
      category: category === "all" ? undefined : category,
      date_from, date_to,
    };
  };

  const fetchSummary = useCallback(() => {
    setLoadingSummary(true);
    return adminService.getMarketPulseSummary(buildFilters())
      .then(setSummary)
      .catch(console.error)
      .finally(() => setLoadingSummary(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, priceCategory, category, geoPath, datePreset, customFrom, customTo]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const fetchGeoRollup = useCallback(() => {
    if (currentGeoLevel === undefined) return undefined; // already at city, deepest level — nothing further to roll up
    setLoadingGeo(true);
    return adminService.getMarketPulseGeoRollup({ ...buildFilters(), level: currentGeoLevel })
      .then((res) => setGeoRows(res.rows))
      .catch(console.error)
      .finally(() => setLoadingGeo(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, priceCategory, category, geoPath, datePreset, customFrom, customTo]);

  useEffect(() => {
    fetchGeoRollup();
  }, [fetchGeoRollup]);

  const fetchPriceTrend = useCallback(() => {
    if (trendGeoKey === "all") { setTrendPoints([]); return undefined; }
    if (isRouteCategory && trendOriginKey === "all") { setTrendPoints([]); return undefined; }
    setLoadingTrend(true);
    const filters = buildFilters();
    return adminService.getMarketPulsePriceTrend({
      geo_level: trendGeoLevel,
      geo_key: trendGeoKey,
      origin_geo_level: isRouteCategory ? trendOriginLevel : undefined,
      origin_geo_key: isRouteCategory ? trendOriginKey : undefined,
      category: trendCategory === "all" ? undefined : trendCategory,
      tab: filters.tab,
      date_from: filters.date_from,
      date_to: filters.date_to,
      bucket: datePreset === "7d" ? "day" : "week",
    })
      .then((res) => setTrendPoints(res.points))
      .catch(console.error)
      .finally(() => setLoadingTrend(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendGeoLevel, trendGeoKey, trendOriginLevel, trendOriginKey, trendCategory, tab, datePreset, customFrom, customTo]);

  useEffect(() => {
    fetchPriceTrend();
  }, [fetchPriceTrend]);

  const handleRefreshAll = useCallback(() => {
    return Promise.all([fetchFilterOptions(), fetchSummary(), fetchGeoRollup(), fetchPriceTrend()]).then(() => {});
  }, [fetchFilterOptions, fetchSummary, fetchGeoRollup, fetchPriceTrend]);

  const drillInto = (row: GeoRollupRow) => {
    if (geoPath.length >= GEO_LEVEL_ORDER.length - 1) return; // already at city, nothing deeper
    setGeoPath((p) => [...p, { level: currentGeoLevel, key: row.key, name: row.name }]);
  };
  const jumpTo = (index: number) => setGeoPath((p) => p.slice(0, index)); // index=-1-ish via slice(0,0) resets to root

  const handleExport = async () => {
    setExporting(true);
    try {
      await adminService.exportMarketPulseExcel(buildFilters());
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  // Value options for the Price Trend geo picker, shaped to whichever level is selected —
  // continents/subregions are flat name lists, countries carry {iso2, name}, cities are a
  // flat name list too (no country scoping on this picker — a global "Bangkok" pick is fine,
  // the chart is scoped by geo_level+geo_key alone, same as the drill-down table's own key).
  const trendGeoOptions = (() => {
    if (!filterOptions) return [];
    if (trendGeoLevel === "continent") return filterOptions.continents.map((c) => ({ value: c, label: c }));
    if (trendGeoLevel === "subregion") return filterOptions.subregions.map((s) => ({ value: s, label: s }));
    if (trendGeoLevel === "country") return filterOptions.countries.map((c) => ({ value: c.iso2, label: c.name }));
    return filterOptions.cities.map((c) => ({ value: c, label: c }));
  })();

  // Route origin picker — country/city only (a flight's origin doesn't need
  // continent/region granularity the way a destination browse might).
  const trendOriginOptions = (() => {
    if (!filterOptions) return [];
    if (trendOriginLevel === "country") return filterOptions.countries.map((c) => ({ value: c.iso2, label: c.name }));
    return filterOptions.cities.map((c) => ({ value: c, label: c }));
  })();

  // Grid heatmap: continents (rows) x price tiers (columns) — always shows continent-level
  // spread regardless of drill depth, so drilling into one country doesn't lose the big
  // picture; it's driven by summary.heatmap which already respects the current filters.
  const heatmapContinents = Array.from(new Set((summary?.heatmap || []).map((h) => h.continent))).sort();
  const priceTiers: PriceCategory[] = ["budget", "mid", "premium", "luxury"];
  const heatCell = (cont: string, cat: PriceCategory) =>
    summary?.heatmap.find((h) => h.continent === cont && h.price_category === cat)?.count ?? 0;
  const maxHeat = Math.max(1, ...(summary?.heatmap || []).map((h) => h.count));

  return (
    <div>
      <AdminPageHeader
        title="Market Pulse"
        subtitle="Demand signal captured from itinerary, flyer, video, and RAG chat generations, plus uploaded deals — geo + price, drillable."
        actions={<RefreshButton onRefresh={handleRefreshAll} />}
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Total Signals</p>
          <p className="text-2xl font-bold text-white">{loadingSummary ? "…" : nfmt(summary?.total ?? 0)}</p>
        </div>
        {(summary?.by_tab || []).map((t) => (
          <div key={t.tab} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-1">{TAB_LABEL[t.tab]}</p>
            <p className="text-2xl font-bold text-white">{nfmt(t.count)}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Select value={tab} onChange={setTab} placeholder="All tabs"
          options={(filterOptions?.tabs || []).map((t) => ({ value: t, label: TAB_LABEL[t] }))} />
        <Select value={priceCategory} onChange={setPriceCategory} placeholder="All price tiers"
          options={priceTiers.map((c) => ({ value: c, label: PRICE_TIER_LABEL[c] }))} />
        <Select value={category} onChange={setCategory} placeholder="All deal categories"
          options={(filterOptions?.categories || []).map((c) => ({ value: c, label: DEAL_CATEGORY_LABEL[c] }))} />

        <div className="relative">
          <button
            type="button"
            onClick={() => setDateMenuOpen((o) => !o)}
            className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-300 hover:border-gray-700 transition-colors"
          >
            {DATE_PRESETS.find((p) => p.key === datePreset)?.label ?? "All time"}
            <span className="text-gray-600 text-xs">▾</span>
          </button>
          {dateMenuOpen && (
            <div
              className="absolute right-0 left-auto max-sm:left-1/2 max-sm:right-auto max-sm:-translate-x-1/2 mt-2 w-64 max-w-[calc(100vw-2rem)] bg-gray-900 border border-gray-800 rounded-lg shadow-xl z-20 p-2"
              onMouseLeave={() => setDateMenuOpen(false)}
            >
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => { setDatePreset(p.key); if (p.key !== "custom") setDateMenuOpen(false); }}
                  className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                    datePreset === p.key ? "bg-indigo-500/20 text-indigo-300" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  {p.label}
                </button>
              ))}
              {datePreset === "custom" && (
                <div className="mt-2 pt-2 border-t border-gray-800 space-y-2">
                  <label className="block">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide">From</span>
                    <input type="datetime-local" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                      style={{ colorScheme: "dark" }}
                      className="mt-1 w-full bg-gray-950 border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500/50" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide">To</span>
                    <input type="datetime-local" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                      style={{ colorScheme: "dark" }}
                      className="mt-1 w-full bg-gray-950 border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500/50" />
                  </label>
                  <button type="button" onClick={() => setDateMenuOpen(false)}
                    className="w-full mt-1 px-3 py-1.5 rounded-md text-xs font-medium bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 transition-colors">
                    Apply
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Graph + Heatmap side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="flex items-baseline justify-between mb-3">
            <p className="text-white font-semibold text-sm">Signals over time</p>
            <span className="text-[11px] text-gray-500">{DATE_PRESET_LABEL_SHORT[datePreset]}</span>
          </div>
          <div className="h-64">
            {loadingSummary ? (
              <div className="h-full flex items-center justify-center"><Spinner /></div>
            ) : (summary?.timeseries || []).length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-600 text-sm">No data for this filter</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={summary?.timeseries} margin={{ bottom: 8 }}>
                  <defs>
                    <linearGradient id="mpArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis
                    dataKey="date"
                    stroke="#6b7280"
                    fontSize={11}
                    tickFormatter={fmtAxisDate}
                    minTickGap={28}
                    interval="preserveStartEnd"
                    tickMargin={8}
                  />
                  <YAxis stroke="#6b7280" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#111827", border: "1px solid #1f2937", fontSize: 12 }}
                    labelFormatter={(label) => fmtAxisDate(String(label ?? ""))}
                  />
                  <Area type="monotone" dataKey="count" stroke="#6366f1" fill="url(#mpArea)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-white font-semibold text-sm mb-3">Continent × price tier</p>
          {loadingSummary ? (
            <div className="h-64 flex items-center justify-center"><Spinner /></div>
          ) : heatmapContinents.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-gray-600 text-sm">No data for this filter</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left py-1.5 px-2 text-gray-500 font-medium">Continent</th>
                    {priceTiers.map((c) => (
                      <th key={c} className="text-center py-1.5 px-2 text-gray-500 font-medium">{PRICE_TIER_LABEL[c]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatmapContinents.map((cont) => (
                    <tr key={cont} className="border-t border-gray-800/50">
                      <td className="py-2 px-2 text-gray-300 whitespace-nowrap">{cont}</td>
                      {priceTiers.map((cat) => {
                        const v = heatCell(cont, cat);
                        const intensity = v / maxHeat;
                        return (
                          <td key={cat} className="py-2 px-2 text-center">
                            <div
                              className="rounded-md py-1.5 mx-auto text-white font-medium"
                              style={{
                                backgroundColor: v === 0 ? "transparent" : PRICE_TIER_COLOR[cat],
                                opacity: v === 0 ? 1 : 0.25 + intensity * 0.75,
                                color: v === 0 ? "#4b5563" : "#fff",
                              }}
                            >
                              {v === 0 ? "—" : v}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Price Trend — the ONLY chart sourced from real append-only price history rather
          than market_signal_log's current-state snapshot, so an edited deal's old prices
          still show up as real points instead of vanishing on every update. */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <p className="text-white font-semibold text-sm">
            Price trend — {isRouteCategory ? "route (origin × destination)" : "destination"} × category
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={trendCategory} onChange={(v) => {
              setTrendCategory(v);
              const willBeRoute = v === "flight";
              if (!willBeRoute) { setTrendOriginKey("all"); }
              // Route categories only ever capture country/city (see the route hooks) —
              // drop back to country if a continent/region was picked on a different category.
              if (willBeRoute && (trendGeoLevel === "continent" || trendGeoLevel === "subregion")) {
                setTrendGeoLevel("country"); setTrendGeoKey("all");
              }
            }} placeholder="All categories"
              // Transfer/cruise deliberately left out of this picker for now — capture is
              // fixed and real (see the TBD-placeholder fix), but not surfaced here yet.
              options={(filterOptions?.categories || [])
                .filter((c) => c !== "transfer" && c !== "cruise")
                .map((c) => ({ value: c, label: DEAL_CATEGORY_LABEL[c] }))} />

            {isRouteCategory && (
              <>
                <span className="text-gray-600 text-xs px-1">from</span>
                <Select value={trendOriginLevel} onChange={(v) => { setTrendOriginLevel(v as "country" | "city"); setTrendOriginKey("all"); }}
                  placeholder="Level" options={[{ value: "country", label: "Country" }, { value: "city", label: "City" }]} />
                <Select value={trendOriginKey} onChange={setTrendOriginKey} placeholder={`Pick a ${trendOriginLevel}`}
                  options={trendOriginOptions} />
                <span className="text-gray-600 text-xs px-1">to</span>
              </>
            )}

            <Select
              value={trendGeoLevel}
              onChange={(v) => { setTrendGeoLevel(v as GeoRollupLevel); setTrendGeoKey("all"); }}
              placeholder="Level"
              options={(isRouteCategory ? GEO_LEVEL_ORDER.filter((l) => l === "country" || l === "city") : GEO_LEVEL_ORDER)
                .map((l) => ({ value: l, label: GEO_LEVEL_LABEL[l] }))}
            />
            <Select value={trendGeoKey} onChange={setTrendGeoKey} placeholder={`Pick a ${GEO_LEVEL_LABEL[trendGeoLevel].toLowerCase()}`}
              options={trendGeoOptions} />
          </div>
        </div>
        <div className="h-64">
          {isRouteCategory && trendOriginKey === "all" ? (
            <div className="h-full flex items-center justify-center text-gray-600 text-sm">
              This category's price depends on the route — pick a "from" first
            </div>
          ) : trendGeoKey === "all" ? (
            <div className="h-full flex items-center justify-center text-gray-600 text-sm">
              Pick a {GEO_LEVEL_LABEL[trendGeoLevel].toLowerCase()}{isRouteCategory ? " (destination)" : ""} to see its price trend
            </div>
          ) : loadingTrend ? (
            <div className="h-full flex items-center justify-center"><Spinner /></div>
          ) : trendPoints.length === 0 ? (
            <div className="h-full flex items-center justify-center text-gray-600 text-sm">No listed-price history for this filter yet</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendPoints} margin={{ bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  fontSize={11}
                  tickFormatter={fmtAxisDate}
                  minTickGap={28}
                  interval="preserveStartEnd"
                  tickMargin={8}
                />
                <YAxis stroke="#6b7280" fontSize={11} tickFormatter={(v) => `₹${nfmt(v)}`} width={70} />
                <Tooltip
                  contentStyle={{ background: "#111827", border: "1px solid #1f2937", fontSize: 12 }}
                  labelFormatter={(label) => fmtAxisDate(String(label ?? ""))}
                  formatter={(value, name) => [`₹${nfmt(Number(value ?? 0))}`, String(name)]}
                />
                <Line type="monotone" dataKey="price_p90_inr" name="p90" stroke="#374151" strokeWidth={1} dot={false} strokeDasharray="4 3" />
                <Line type="monotone" dataKey="price_median_inr" name="median" stroke="#6366f1" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="price_p10_inr" name="p10" stroke="#374151" strokeWidth={1} dot={false} strokeDasharray="4 3" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Geo drill-down — replaces a flat per-signal table. Never shows an individual
          deal/user row; always an aggregated group at the current geo level, with a
          price RANGE (never an average — mixed currencies and old/new listings make an
          average meaningless) plus category and price-tier breakdowns. */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800 flex-wrap gap-2">
          <div className="flex items-center gap-1.5 text-sm">
            <button
              type="button"
              onClick={() => jumpTo(0)}
              className={`px-2 py-1 rounded-md transition-colors ${geoPath.length === 0 ? "text-white font-medium" : "text-indigo-400 hover:text-indigo-300"}`}
            >
              All regions
            </button>
            {geoPath.map((step, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <span className="text-gray-600">/</span>
                <button
                  type="button"
                  onClick={() => jumpTo(i + 1)}
                  className={`px-2 py-1 rounded-md transition-colors ${i === geoPath.length - 1 ? "text-white font-medium" : "text-indigo-400 hover:text-indigo-300"}`}
                >
                  {step.name}
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-gray-500">
              Current price = recency-weighted real listings (not just the last one), always up to date. Recent range = <span className="text-gray-300">{DATE_PRESET_LABEL_SHORT[datePreset]}</span> spread. Searched budgets excluded from both.
            </span>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || (summary?.total ?? 0) === 0}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 disabled:opacity-40 transition-colors whitespace-nowrap"
            >
              {exporting ? "Exporting…" : "Export Excel"}
            </button>
          </div>
        </div>

        {currentGeoLevel === undefined ? (
          <div className="py-16 text-center text-gray-500 text-sm">
            You're at city level — the deepest this breaks down to. Use the summary cards and graph above for this city's numbers.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">{GEO_LEVEL_LABEL[currentGeoLevel]}</th>
                  <th className="text-right py-3 px-4 text-gray-500 font-medium">Signals</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Current price by category (₹, recent range below)</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Price tiers</th>
                  <th className="text-right py-3 px-4 text-gray-500 font-medium">Overall current price (₹, excl. flight/transfer/cruise)</th>
                  <th className="py-3 px-4" />
                </tr>
              </thead>
              <tbody>
                {loadingGeo ? (
                  <tr><td colSpan={6} className="py-16 text-center text-gray-500"><Spinner /></td></tr>
                ) : geoRows.length === 0 ? (
                  <tr><td colSpan={6} className="py-16 text-center text-gray-500">No signals captured for this filter yet</td></tr>
                ) : (
                  geoRows.map((row) => (
                    <tr
                      key={row.key}
                      onClick={() => drillInto(row)}
                      className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors cursor-pointer"
                    >
                      <td className="py-3 px-4 text-white font-medium">{row.name}</td>
                      <td className="py-3 px-4 text-right text-white">{nfmt(row.count)}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-2">
                          {row.by_category.length === 0 ? (
                            <span className="text-gray-600 text-xs">—</span>
                          ) : row.by_category.slice(0, 3).map((c) => (
                            <div key={c.category} className="flex flex-col gap-0.5">
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-500/10 text-indigo-400 w-fit">
                                {DEAL_CATEGORY_LABEL[c.category]} {c.count}
                              </span>
                              {c.is_route_dependent ? (
                                <span className="text-[10px] text-gray-500 italic whitespace-nowrap">
                                  route-dependent{c.category === "flight" ? " — see chart below" : " — not shown yet"}
                                </span>
                              ) : (
                                <>
                                  <span className={`text-[10px] font-medium whitespace-nowrap ${c.current_price_low_confidence ? "text-amber-400/80" : "text-emerald-400"}`}>
                                    {fmtInr(c.current_price_inr)}
                                    {c.current_price_at && <span className="opacity-60"> · {fmtAsOf(c.current_price_at)}</span>}
                                  </span>
                                  {c.current_price_low_confidence && c.current_price_inr != null && (
                                    <span className="text-[9px] text-amber-400/70 whitespace-nowrap">low confidence — only {c.current_price_n} listing{c.current_price_n === 1 ? "" : "s"}</span>
                                  )}
                                  <span className="text-[10px] text-gray-500 whitespace-nowrap">{fmtBand(c.price_p10_inr, c.price_p90_inr)}</span>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {row.by_price_tier.map((t) => (
                            <span
                              key={t.price_category}
                              className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                              style={{ backgroundColor: `${PRICE_TIER_COLOR[t.price_category]}20`, color: PRICE_TIER_COLOR[t.price_category] }}
                            >
                              {PRICE_TIER_LABEL[t.price_category]} {t.count}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right whitespace-nowrap text-xs">
                        <div className={`font-semibold ${row.current_price_low_confidence ? "text-amber-400/80" : "text-gray-200"}`}>
                          {fmtInr(row.current_price_inr)}
                          {row.current_price_at && <span className="block text-[10px] text-gray-500 font-normal">as of {fmtAsOf(row.current_price_at)}</span>}
                        </div>
                        {row.current_price_low_confidence && row.current_price_inr != null && (
                          <div className="text-[9px] text-amber-400/70">low confidence — {row.current_price_n} listing{row.current_price_n === 1 ? "" : "s"}</div>
                        )}
                        <div className="text-gray-600 mt-0.5">recent: {fmtBand(row.price_p10_inr, row.price_p90_inr)}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {geoPath.length < GEO_LEVEL_ORDER.length - 1 ? "›" : ""}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Signal breakdown by source — same geo drill-down/filters as the main table above
          (one shared geoPath/geoRows, so drilling into a region narrows both at once), but
          this one never blends: deal (real listings) + rag_chat/itinerary/flyer/video
          (search/generation demand, not a listing) each get their own badge instead of one
          combined number. Totals here always equal the main table's Signals count for the
          same row — same source rows, just fully split apart instead of blended. */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mt-6">
        <div className="px-5 py-3 border-b border-gray-800">
          <p className="text-white font-semibold text-sm">Signals by Source</p>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Every signal, split apart — deals (real listings) plus RAG chat/itinerary/flyer/video (search &amp; generation demand, not a listing).
          </p>
        </div>

        {currentGeoLevel === undefined ? (
          <div className="py-16 text-center text-gray-500 text-sm">
            You're at city level — the deepest this breaks down to. Use the summary cards above for this city's numbers.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">{GEO_LEVEL_LABEL[currentGeoLevel]}</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">By source</th>
                  <th className="text-right py-3 px-4 text-gray-500 font-medium">Total</th>
                  <th className="py-3 px-4" />
                </tr>
              </thead>
              <tbody>
                {loadingGeo ? (
                  <tr><td colSpan={4} className="py-16 text-center text-gray-500"><Spinner /></td></tr>
                ) : geoRows.length === 0 ? (
                  <tr><td colSpan={4} className="py-16 text-center text-gray-500">No signals captured for this filter yet</td></tr>
                ) : (
                  geoRows.map((row) => {
                    const bySource = SOURCE_TABS.map((t) => ({ tab: t, count: tabCount(row, t) })).filter((t) => t.count > 0);
                    const total = bySource.reduce((sum, t) => sum + t.count, 0);
                    return (
                      <tr
                        key={row.key}
                        onClick={() => drillInto(row)}
                        className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors cursor-pointer"
                      >
                        <td className="py-3 px-4 text-white font-medium">{row.name}</td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1.5">
                            {bySource.length === 0 ? (
                              <span className="text-gray-600 text-xs">—</span>
                            ) : bySource.map((t) => (
                              <span
                                key={t.tab}
                                className="px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
                                style={{ backgroundColor: `${SOURCE_TAB_COLOR[t.tab]}20`, color: SOURCE_TAB_COLOR[t.tab] }}
                              >
                                {TAB_LABEL[t.tab]} {nfmt(t.count)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right text-white font-semibold">{nfmt(total)}</td>
                        <td className="py-3 px-4 text-gray-600">
                          {geoPath.length < GEO_LEVEL_ORDER.length - 1 ? "›" : ""}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMarketPulsePage;
